import React, { useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import About from './components/About';
import Benefits from './components/Benefits';
import Testimonials from './components/Testimonials';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import Chatbot from './components/Chatbot';
import { trackUserBehavior } from './utils/analytics';

const theme = {
  colors: {
    primary: '#0550e9',
    secondary: '#0550e9',
    accent: '#FF6B35',
    dark: '#0A0A0A',
    darkGray: '#1A1A1A',
    mediumGray: '#2A2A2A',
    lightGray: '#F5F5F5',
    white: '#FFFFFF',
    success: '#00FF88',
    warning: '#FFB800'
  },
  fonts: {
    primary: "'Inter', sans-serif",
    display: "'Orbitron', monospace"
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  }
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${props => props.theme.fonts.primary};
    background: ${props => props.theme.colors.dark};
    color: ${props => props.theme.colors.white};
    overflow-x: hidden;
    line-height: 1.6;
  }

  html {
    scroll-behavior: smooth;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.darkGray};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.secondary};
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  position: relative;
`;

function App() {
  useEffect(() => {
    // Inicializar tracking de comportamento
    trackUserBehavior.init();
    
    // Track page view
    trackUserBehavior.trackEvent('page_view', {
      page: 'landing_page',
      timestamp: new Date().toISOString()
    });

    // Track scroll depth
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent % 25 === 0 && scrollPercent > 0) {
        trackUserBehavior.trackEvent('scroll_depth', {
          depth: scrollPercent,
          timestamp: new Date().toISOString()
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppContainer>
        <Hero />
        <Problem />
        <Solution />
        <About />
        <Benefits />
        <Testimonials />
        <Pricing />
        <FAQ />
        <Footer />
        <Chatbot />
      </AppContainer>
    </ThemeProvider>
  );
}

export default App;